/**
 * AppICEMAP — Distance units
 *
 * Distances travel through the app in KILOMETRES — that is the canonical unit
 * for anything stored or compared (filters.radiusKm, haversine results). Miles
 * exist only at the edges, when a number is shown to the user or picked in the
 * settings, so there is never an ambiguous value floating around.
 */

const UNIT_KEY = "appicemap-distance-unit";

/** Kilometres in one mile */
const KM_PER_MILE = 1.609344;

/** Feet in one kilometre */
const FEET_PER_KM = 3280.84;

/** @type {'km' | 'mi' | null} cached preference */
let cached = null;

/**
 * Default for a first-time user: MILES.
 *
 * The browser language is a bad signal for this app. Its community is
 * Spanish-speaking but lives in the United States, so most devices report
 * es-ES / es-MX while the person reading the screen thinks in miles. Locale
 * detection would hand them kilometres, which is the wrong guess almost every
 * time — hence a fixed default plus the toggle in settings.
 *
 * @returns {'km' | 'mi'}
 */
function detectDefaultUnit() {
	return "mi";
}

/**
 * Current unit preference.
 * @returns {'km' | 'mi'}
 */
export function getUnit() {
	if (cached) return cached;

	try {
		const saved = localStorage.getItem(UNIT_KEY);
		if (saved === "km" || saved === "mi") {
			cached = saved;
			return cached;
		}
	} catch {
		/* private mode */
	}

	cached = detectDefaultUnit();
	return cached;
}

/**
 * Store the unit preference.
 * @param {'km' | 'mi'} unit
 */
export function setUnit(unit) {
	cached = unit === "mi" ? "mi" : "km";
	try {
		localStorage.setItem(UNIT_KEY, cached);
	} catch {
		/* private mode — the setting just won't survive the reload */
	}
}

/** Short label for the current unit, e.g. "mi" */
export function unitLabel(unit = getUnit()) {
	return unit === "mi" ? "mi" : "km";
}

/**
 * @param {number} km
 * @returns {number} the same distance in miles
 */
export function kmToMiles(km) {
	return km / KM_PER_MILE;
}

/**
 * @param {number} miles
 * @returns {number} the same distance in kilometres
 */
export function milesToKm(miles) {
	return miles * KM_PER_MILE;
}

/**
 * Format a distance for display, switching to the small unit (metres or feet)
 * when the number would otherwise read as a meaningless "0.1".
 *
 * @param {number} km - Distance in kilometres
 * @param {'km' | 'mi'} [unit]
 * @returns {string}
 */
export function formatDistance(km, unit = getUnit()) {
	if (!Number.isFinite(km)) return "";

	if (unit === "mi") {
		const miles = kmToMiles(km);
		if (miles < 0.19) return `${Math.round(km * FEET_PER_KM)} ft`;
		return `${miles.toFixed(1)} mi`;
	}

	if (km < 1) return `${Math.round(km * 1000)} m`;
	return `${km.toFixed(1)} km`;
}

/**
 * Radius choices offered in settings, in the user's unit. Values stay in km so
 * the stored preference never depends on which unit was on screen.
 *
 * @param {'km' | 'mi'} [unit]
 * @returns {Array<{ km: number|null, label: string }>}
 */
export function radiusOptions(unit = getUnit()) {
	const steps =
		unit === "mi"
			? [
					{ km: milesToKm(1), label: "1" },
					{ km: milesToKm(2), label: "2" },
					{ km: milesToKm(5), label: "5" },
					{ km: milesToKm(10), label: "10" },
					{ km: milesToKm(25), label: "25" },
				]
			: [
					{ km: 1, label: "1" },
					{ km: 2, label: "2" },
					{ km: 5, label: "5" },
					{ km: 10, label: "10" },
					{ km: 25, label: "25" },
				];

	return [...steps, { km: null, label: "∞" }];
}

/**
 * Describe a stored radius in the user's unit, e.g. "5 mi".
 * @param {number|null} km
 * @param {'km' | 'mi'} [unit]
 * @returns {string}
 */
export function formatRadius(km, unit = getUnit()) {
	if (km === null || km === undefined) return "Sin límite";
	const value = unit === "mi" ? kmToMiles(km) : km;
	// The stored value comes from a fixed step, so it lands on a round number
	return `${Math.round(value * 10) / 10} ${unitLabel(unit)}`;
}
